import React from 'react';
import {
  Alert, Button, Collapse, Table, Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { downloadText } from '../../../../utils/download-blob';
import { IUploadStepProps } from './i-import-wizard';

const { Dragger } = Upload;
const { Panel } = Collapse;

const MAX_VISIBLE_PARSE_ERRORS = 5;

const TEMPLATE_HEADER = 'code,description,Color,Size\n';

const CSV_EXAMPLE = [
  '# comments and blank lines are ignored',
  'code,description,Color,Size',
  'AT-1001,Blue crate,Blue,Large',
  'AT-1002,Red pallet,Red,Small',
].join('\n');

const JSON_EXAMPLE = [
  '[',
  '  { "code": "AT-1001", "description": "Blue crate", "Color": "Blue" },',
  '  { "code": "AT-1002", "description": "Red pallet", "Color": "Red" }',
  ']',
].join('\n');

const errorTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#E13333',
};

const exampleStyle: React.CSSProperties = {
  margin: '6px 0 0',
  padding: '10px 12px',
  background: '#F8F8FA',
  border: '1px solid #E1E3E8',
  borderRadius: 8,
  fontSize: 12,
  color: '#535559',
  whiteSpace: 'pre',
  overflowX: 'auto',
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: '#8C909B',
};

const downloadTemplate = (): void => {
  downloadText(TEMPLATE_HEADER, 'asset-types-template.csv');
};

const UploadStep = (props: IUploadStepProps): JSX.Element => {
  const {
    fileName,
    fileError,
    parseErrors,
    busy,
    estimatedRows,
    fileBytes,
    sampleHeader,
    sampleRows,
    sampleIsThin: thin,
    resumable,
    onFileSelected,
    onResume,
  } = props;

  const visibleErrors = parseErrors.slice(0, MAX_VISIBLE_PARSE_ERRORS);
  const hiddenErrorCount = parseErrors.length - visibleErrors.length;

  const sampleColumns = sampleHeader.map((header, index) => ({
    title: header || `Column ${index + 1}`,
    key: `${index}-${header}`,
    render: (unused: unknown, row: string[]) => row[index] ?? '',
    ellipsis: true,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Dragger
        accept=".csv,.tsv,.txt,.json"
        multiple={false}
        disabled={busy}
        showUploadList={false}
        beforeUpload={(file) => {
          onFileSelected(file);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <InboxOutlined style={{ color: '#3800EE' }} />
        </p>
        <p style={{ margin: 0, fontSize: 14, color: '#190952' }}>
          Drag a file here, or click to choose one
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8C909B' }}>
          CSV, TSV or JSON
        </p>
      </Dragger>

      {resumable ? (
        <Alert
          type="info"
          showIcon
          message="An import is already running"
          description={(
            <div>
              {`${resumable.fileName ?? 'A file'} is still being imported. `}
              <Button
                type="link"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => onResume(resumable.jobId)}
              >
                Watch it
              </Button>
            </div>
          )}
        />
      ) : null}

      {fileName ? (
        <div style={{ fontSize: 13, color: '#535559' }}>
          Selected file:
          {' '}
          <span style={{ color: '#190952', fontWeight: 500 }}>{fileName}</span>
          {estimatedRows !== null ? (
            <span style={{ color: '#8C909B' }}>
              {/* an estimate from the prefix; the server reports the real count */}
              {` — about ${estimatedRows.toLocaleString()} rows, `}
              {`${(fileBytes / (1024 * 1024)).toFixed(1)} MB`}
            </span>
          ) : null}
        </div>
      ) : null}

      {thin && sampleHeader.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message="We could only read a little of this file — check the column mapping carefully."
        />
      ) : null}

      {sampleHeader.length > 0 ? (
        <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
          <Table
            size="small"
            columns={sampleColumns}
            dataSource={sampleRows.slice(0, 10)}
            rowKey={(row, index) => `${index ?? 0}`}
            bordered={false}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </div>
      ) : null}

      {fileError ? <div style={errorTextStyle}>{fileError}</div> : null}

      {visibleErrors.length ? (
        <div style={{
          ...errorTextStyle, display: 'flex', flexDirection: 'column', gap: 2,
        }}
        >
          {visibleErrors.map((error) => (
            <span key={`${error.line}-${error.message}`}>
              {`Line ${error.line}: ${error.message}`}
            </span>
          ))}
          {hiddenErrorCount > 0 ? <span>{`…and ${hiddenErrorCount} more`}</span> : null}
        </div>
      ) : null}

      <Collapse ghost>
        <Panel header="Expected format" key="format">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={hintStyle}>
              CSV, TSV or JSON. Lines starting with
              {' '}
              <code>#</code>
              {' '}
              and blank lines are ignored. The first line left is the header.
              Columns are matched by name and can be corrected in the next step.
            </p>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#535559' }}>CSV</div>
              <pre style={exampleStyle}>{CSV_EXAMPLE}</pre>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#535559' }}>JSON</div>
              <pre style={exampleStyle}>{JSON_EXAMPLE}</pre>
            </div>
          </div>
        </Panel>
      </Collapse>

      <div>
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={downloadTemplate}>
          Download CSV template
        </Button>
      </div>
    </div>
  );
};

UploadStep.displayName = 'UploadStep';

export default UploadStep;
